import React, {useEffect, useState} from 'react';
import {View, StyleSheet} from 'react-native';
import Gradient from 'react-native-linear-gradient';
import Slider from '@react-native-community/slider';
import {
  Canvas,
  Path,
  LinearGradient,
  vec,
  Skia,
} from '@shopify/react-native-skia';

import Text from '../Text';
import Divider from '../Divider';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../../theme';
import {COLORS} from '../../../utils/Constants';

const AreaChart = ({data, width, height, minValue, maxValue, max}) => {
  // Structural chart colors are themed; the pink range highlight is
  // decorative brand color (docs/DARK-MODE.md decorative list).
  const {colors} = useTheme();
  // Create path from data
  const path = Skia.Path.Make();

  if (data && data.length > 0) {
    const stepX = width / (data.length - 1);
    const scaleY = height / Math.max(...data, 1);

    // Start from bottom left
    path.moveTo(0, height);

    // Draw area chart
    data.forEach((value, index) => {
      const x = index * stepX;
      const y = height - value * scaleY;
      path.lineTo(x, y);
    });

    // Close path at bottom right
    path.lineTo(width, height);
    path.close();
  }

  // Calculate selected range positions
  const startX = (minValue / max) * width;
  const endX = (maxValue / max) * width;

  // Create selected range path
  const selectedPath = Skia.Path.Make();
  if (data && data.length > 0) {
    const stepX = width / (data.length - 1);
    const scaleY = height / Math.max(...data, 1);

    const startIndex = Math.floor((minValue / max) * (data.length - 1));
    const endIndex = Math.ceil((maxValue / max) * (data.length - 1));

    selectedPath.moveTo(startX, height);

    for (let i = startIndex; i <= endIndex && i < data.length; i++) {
      const x = i * stepX;
      const y = height - data[i] * scaleY;
      selectedPath.lineTo(x, y);
    }

    selectedPath.lineTo(endX, height);
    selectedPath.close();
  }

  return (
    <Canvas style={{width, height}}>
      {/* Background area */}
      <Path path={path} color={colors.backgroundMuted} style="fill" />

      {/* Selected range area with gradient */}
      <Path path={selectedPath} style="fill">
        <LinearGradient
          start={vec(0, 0)}
          end={vec(0, height)}
          colors={[COLORS.pinkGradientOne, colors.background]}
        />
      </Path>

      {/* Dashed line for full area */}
      <Path
        path={path}
        color={colors.borderMuted}
        style="stroke"
        strokeWidth={2}
        strokeDash={[5, 3]}
      />

      {/* Solid line for selected range */}
      <Path
        path={selectedPath}
        color={COLORS.pink}
        style="stroke"
        strokeWidth={2}
      />
    </Canvas>
  );
};

const RangeSlider = ({
  values,
  areaData,
  onValuesUpdated,
  onSliderDragEnd,
  onSliderDragStart,
  max,
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const [minValue, setMinValue] = useState(values[0]);
  const [maxValue, setMaxValue] = useState(values[1]);

  useEffect(() => {
    setMinValue(values[0]);
    setMaxValue(values[1]);
  }, [values]);

  const onMinValueChange = value => {
    const newMin = Math.min(value, maxValue - 1);
    setMinValue(newMin);
    if (onValuesUpdated) {
      onValuesUpdated({values: [newMin, maxValue]});
    }
  };

  const onMaxValueChange = value => {
    const newMax = Math.max(value, minValue + 1);
    setMaxValue(newMax);
    if (onValuesUpdated) {
      onValuesUpdated({values: [minValue, newMax]});
    }
  };

  return (
    <View style={localStyles.container}>
      {/* Area Chart */}
      <View style={localStyles.chartContainer}>
        <AreaChart
          data={areaData}
          width={300}
          height={100}
          minValue={minValue}
          maxValue={maxValue}
          max={max}
        />
      </View>

      {/* Progress Bar */}
      <View style={localStyles.progressBarContainer}>
        <Divider top={0} height={2} color={COLORS.pink} />
      </View>

      {/* Sliders Container */}
      <View style={localStyles.slidersContainer}>
        {/* Min Value Slider */}
        <View style={localStyles.sliderWrapper}>
          <View style={styles.handleContainer(0)}>
            <Gradient
              start={{x: 0.2, y: 0.2}}
              end={{x: 1, y: 1}}
              colors={[colors.surface, colors.link]}
              style={styles.handleView(0)}
            />
            <Text
              variation="Body1SemiBold"
              color={COLORS.pink}
              style={styles.priceText(0)}
            >
              $ {minValue}
            </Text>
          </View>
          <Slider
            style={localStyles.slider}
            minimumValue={0}
            maximumValue={max}
            value={minValue}
            onValueChange={onMinValueChange}
            onSlidingStart={onSliderDragStart}
            onSlidingComplete={onSliderDragEnd}
            minimumTrackTintColor="transparent"
            maximumTrackTintColor="transparent"
            thumbTintColor="transparent"
          />
        </View>

        {/* Max Value Slider */}
        <View style={localStyles.sliderWrapper}>
          <View style={styles.handleContainer(1)}>
            <Gradient
              start={{x: 0.2, y: 0.2}}
              end={{x: 1, y: 1}}
              colors={[colors.surface, colors.link]}
              style={styles.handleView(1)}
            />
            <Text
              variation="Body1SemiBold"
              color={COLORS.pink}
              style={styles.priceText(1)}
            >
              $ {maxValue}
            </Text>
          </View>
          <Slider
            style={localStyles.slider}
            minimumValue={0}
            maximumValue={max}
            value={maxValue}
            onValueChange={onMaxValueChange}
            onSlidingStart={onSliderDragStart}
            onSlidingComplete={onSliderDragEnd}
            minimumTrackTintColor="transparent"
            maximumTrackTintColor="transparent"
            thumbTintColor="transparent"
          />
        </View>
      </View>
    </View>
  );
};

const localStyles = StyleSheet.create({
  container: {
    marginTop: 16,
  },
  chartContainer: {
    height: 100,
    marginBottom: 8,
  },
  progressBarContainer: {
    marginBottom: 8,
  },
  slidersContainer: {
    position: 'relative',
    height: 60,
  },
  sliderWrapper: {
    position: 'absolute',
    width: '100%',
    height: 60,
  },
  slider: {
    width: '100%',
    height: 40,
  },
});

export default RangeSlider;
